export type WorkItemDeliverableStatus = "created" | "updated" | "unchanged";

export type WorkItemDeliverable = {
  status: WorkItemDeliverableStatus;
  deliverablePath: string;
  missingSections: string[];
  missingWorkItemId: boolean;
  missingDefinitionOfDoneChecklist?: boolean;
};

export type WorkItemDeliverableCheck = {
  exists: boolean;
  deliverablePath: string;
};

type DeliverableResponse = {
  ok: boolean;
  deliverable?: WorkItemDeliverable;
  error?: string;
};

type DeliverableStatusResponse = {
  ok: boolean;
  exists?: boolean;
  deliverablePath?: string;
  error?: string;
};

function handleError(response: Response, payload: { error?: string }, fallback: string): never {
  const message = payload?.error ?? `${fallback} (status ${response.status})`;
  throw new Error(message);
}

export async function fetchWorkItemDeliverableStatus(workItemId: string): Promise<WorkItemDeliverableCheck> {
  const response = await fetch(`/api/work-items/${workItemId}/deliverable`);
  const payload = (await response.json()) as DeliverableStatusResponse;

  if (!response.ok || payload.ok === false || typeof payload.exists !== "boolean" || !payload.deliverablePath) {
    handleError(response, payload, "Failed to check WorkItem deliverable");
  }

  return { exists: payload.exists, deliverablePath: payload.deliverablePath };
}

export async function generateWorkItemDeliverable(
  workItemId: string,
  options: { refresh?: boolean } = {},
): Promise<WorkItemDeliverable> {
  const response = await fetch(`/api/work-items/${workItemId}/deliverable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: options.refresh ?? false }),
  });

  const payload = (await response.json()) as DeliverableResponse;

  if (!response.ok || payload.ok === false || !payload.deliverable) {
    handleError(response, payload, "Failed to generate WorkItem deliverable");
  }

  return payload.deliverable;
}
